import { isEqual } from 'lodash';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';

import type { FeatureFlagKey, FeatureFlagData } from '@sb/feathers-types';
import {
  onLoadFeatureFlag,
  updateFeatureFlag,
} from '@sbrc/services/feathers-client';

const LOCAL_OVERRIDE_PREFIX = 'featureFlag:';

export const SHARED_CACHE_KEY = 'featureFlags.shared.cache';

function readPersistedFlagCache(): Partial<FeatureFlagData> {
  try {
    const raw = window.localStorage.getItem(SHARED_CACHE_KEY);

    return raw ? (JSON.parse(raw) as Partial<FeatureFlagData>) : {};
  } catch {
    // A corrupt or unreadable cache is no worse than an empty one, and it must
    // never take the app down: every consumer reads this on first render.
    return {};
  }
}

/**
 * Process-wide shared feature-flag cache.
 *
 * Every consumer reads this one snapshot, so two components can never disagree
 * about a flag. They used to hold a copy each (`useLocalStorageState` per hook
 * call) kept in sync only by `storage` events, which let them drift apart: a
 * component that mounted while the cache was still empty and whose own
 * `featureFlags` find never resolved — the socket saturated or reconnecting —
 * kept reading `{}` and rendered as if every flag were off, while its
 * already-populated siblings rendered as if on. With `appRedesign` that shows
 * up as one half of the app in the redesign and the other half in the legacy
 * UI, stuck that way until the app is restarted.
 *
 * Persisted to localStorage so a reload starts from the last known values
 * rather than from "everything off".
 */
let sharedFlagCache: Partial<FeatureFlagData> | null = null;
const sharedFlagCacheListeners = new Set<() => void>();
let unsubscribeFromFlagService: (() => void) | null = null;

function getSharedFlagCache(): Partial<FeatureFlagData> {
  if (sharedFlagCache === null) {
    sharedFlagCache = readPersistedFlagCache();
  }

  return sharedFlagCache;
}

function setSharedFlagCache(flags: Partial<FeatureFlagData>) {
  // Referential stability matters: `getSharedFlagCache` is a
  // `useSyncExternalStore` snapshot, so handing back a new object for an
  // unchanged value would re-render every consumer on every service emission.
  if (isEqual(flags, getSharedFlagCache())) {
    return;
  }

  sharedFlagCache = flags;

  try {
    window.localStorage.setItem(SHARED_CACHE_KEY, JSON.stringify(flags));
  } catch {
    // Persistence is a nicety (a full or partitioned store on iOS shouldn't
    // cost us the in-memory value); listeners still get the new flags.
  }

  sharedFlagCacheListeners.forEach((listener) => listener());
}

function subscribeToSharedFlagCache(listener: () => void): () => void {
  sharedFlagCacheListeners.add(listener);

  // One subscription to the flag service for the whole app, opened with the
  // first consumer. Previously each consumer opened its own and wrote the
  // result back to localStorage, so a single flag load cost N writes and N
  // synthetic storage events, each of which every other consumer handled.
  if (!unsubscribeFromFlagService) {
    unsubscribeFromFlagService = onLoadFeatureFlag(setSharedFlagCache);
  }

  return () => {
    sharedFlagCacheListeners.delete(listener);

    if (sharedFlagCacheListeners.size === 0) {
      unsubscribeFromFlagService?.();
      unsubscribeFromFlagService = null;
    }
  };
}

function useSharedFlagCache(): Partial<FeatureFlagData> {
  return useSyncExternalStore(
    subscribeToSharedFlagCache,
    getSharedFlagCache,
    getSharedFlagCache,
  );
}

/**
 * Drops the process-wide cache. Only for tests, which would otherwise carry one
 * case's flags into the next.
 */
export function resetSharedFeatureFlagCache() {
  sharedFlagCache = null;
  unsubscribeFromFlagService?.();
  unsubscribeFromFlagService = null;
  sharedFlagCacheListeners.clear();
}

function getLocalStorageKey(key: string) {
  return `${LOCAL_OVERRIDE_PREFIX}${key}`;
}

// Reads the per-user overrides the "Me" toggle has written to localStorage
// (`featureFlag:<key>` === 'on'). Overrides are additive: only an 'on' entry
// is recorded, so it can force a flag on but never off — any other value is
// ignored and the shared cache value stands. This keeps the merged map
// consistent with useFeatureFlag, whose writer only ever stores 'on' or
// removes the key.
function readLocalFeatureFlagOverrides(): Partial<FeatureFlagData> {
  const overrides: Record<string, boolean> = {};

  for (let i = 0; i < window.localStorage.length; i += 1) {
    const lsKey = window.localStorage.key(i);

    if (
      lsKey?.startsWith(LOCAL_OVERRIDE_PREFIX) &&
      window.localStorage.getItem(lsKey) === 'on'
    ) {
      overrides[lsKey.slice(LOCAL_OVERRIDE_PREFIX.length)] = true;
    }
  }

  return overrides as Partial<FeatureFlagData>;
}

function useLocalFeatureFlagOverrides(): Partial<FeatureFlagData> {
  const [overrides, setOverrides] = useState(readLocalFeatureFlagOverrides);

  useEffect(() => {
    const listener = (ev: StorageEvent) => {
      if (
        ev.storageArea === window.localStorage &&
        (ev.key === null || ev.key.startsWith(LOCAL_OVERRIDE_PREFIX))
      ) {
        setOverrides(readLocalFeatureFlagOverrides());
      }
    };

    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, []);

  return overrides;
}

// Effective feature-flag map for the current user: the shared cache with
// per-user local overrides applied on top. Use this for UI gating so the "Me"
// toggle is honored. For the canonical shared/server value regardless of a
// local override, use useSharedFeatureFlag.
export const useAllFeatureFlags = (): Partial<FeatureFlagData> => {
  const flagCache = useSharedFlagCache();
  const localOverrides = useLocalFeatureFlagOverrides();

  return useMemo(
    () => ({ ...flagCache, ...localOverrides }),
    [flagCache, localOverrides],
  );
};

export const useSharedFeatureFlag = (key: FeatureFlagKey): boolean | null => {
  const flagCache = useSharedFlagCache();

  return flagCache[key] ?? null;
};

export function useLocalFeatureFlagValue(key: FeatureFlagKey): string | null {
  const lsKey = getLocalStorageKey(key);

  const [value, setValue] = useState(window.localStorage.getItem(lsKey));

  useEffect(() => {
    const listener = (ev: StorageEvent) => {
      if (ev.storageArea === window.localStorage && ev.key === lsKey) {
        setValue(ev.newValue);
      }
    };

    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [lsKey]);

  return value;
}

export const useLocalFeatureFlag = (key: FeatureFlagKey): boolean =>
  useLocalFeatureFlagValue(key) === 'on';

export function useFeatureFlag(key: FeatureFlagKey): boolean {
  const local = useLocalFeatureFlagValue(key);
  const shared = !!useSharedFeatureFlag(key);

  if (local !== null) return local === 'on';

  return shared;
}

export async function setSharedFeatureFlag(
  key: FeatureFlagKey,
  value: boolean,
) {
  await updateFeatureFlag(key, value);
}

export function setLocalFeatureFlag(key: FeatureFlagKey, value: boolean) {
  const lsKey = getLocalStorageKey(key);

  if (value) {
    window.localStorage.setItem(lsKey, 'on');
  } else {
    window.localStorage.removeItem(lsKey);
  }

  // other browser tabs receive this event automatically,
  // but we need to emit a synthetic event to tell the event listeners in _this_ tab
  window.dispatchEvent(
    new StorageEvent('storage', {
      key: lsKey,
      newValue: value ? 'on' : null,
      storageArea: window.localStorage,
    }),
  );
}

/**
 * Utility function to get feature flag value directly from localStorage cache
 * Can be used outside of React components
 */
export function getFeatureFlagFromCache(key: string): boolean | null {
  try {
    const cache = localStorage.getItem(SHARED_CACHE_KEY);

    if (cache) {
      const parsed = JSON.parse(cache);

      return parsed[key] ?? null;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to read feature flags from localStorage:', error);
  }

  return null;
}
